import { Input, tokens, TooltipContent, TooltipRoot, TooltipTrigger } from "@blurb/codex-react";
import { createStyles, Divider, Stack } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import { Label } from "../../components/Typography";
import { RpiTextField } from "../../components/RpiTextField";
import { formatCurrency } from "../../utils/misc.util";
import { getBookPriceInfoUSD, PriceInfo } from "../../utils/pricing.util";

export const PRODUCT_NAME_REQUIRED_ERROR = "Please enter a product name";
export const PRODUCT_SKU_REQUIRED_ERROR = "Please enter a Product SKU";
const DUPLICATE_SKU_ERROR = "A product with that SKU already exists";

/** Maps a save failure to a user-facing message, or undefined when it isn't a GraphQL error. */
export function getProductSaveErrorMessage(error: unknown): string | undefined {
  if (!isGraphQLError(error)) {
    return undefined;
  }
  return error.message.includes("sku already exists") ? DUPLICATE_SKU_ERROR : error.message;
}

const useNameAndSkuStyles = createStyles({
  fieldContainer: {
    gap: tokens.spacing[6],
  },
});

type NameAndSkuEditorProps = {
  openapiProduct: GraphQL.OpenApiProductWithPricingQuery["openapiProduct"] & { openapiSku: string };
  productName: string;
  productNameError?: string;
  onProductNameChanged: (newProductName: string) => void;
  customerSku: string;
  customerSkuError?: string;
  onCustomerSkuChanged: (newSku: string) => void;
  loading: boolean;
};

export function NameAndSkuEditor({
  openapiProduct,
  productName,
  productNameError,
  onProductNameChanged,
  customerSku,
  customerSkuError,
  onCustomerSkuChanged,
  loading,
}: NameAndSkuEditorProps) {
  const { classes } = useNameAndSkuStyles();
  const priceInfo = getBookPriceInfoUSD(openapiProduct);

  return (
    <Stack className={classes.fieldContainer}>
      <Input
        label="Product Name"
        value={productName}
        data-testid="customerProductNameInput"
        error={productNameError}
        maxLength={128}
        onChange={(event) => onProductNameChanged(event.currentTarget.value)}
        disabled={loading}
      />
      <Input
        label="Product SKU"
        value={customerSku}
        error={customerSkuError}
        maxLength={64}
        onChange={(event) => onCustomerSkuChanged(event.currentTarget.value)}
        disabled={loading}
      />
      <AdditionalInfo openapiSku={openapiProduct.openapiSku} customerSku={customerSku} priceInfo={priceInfo} />
    </Stack>
  );
}

const useAdditionalInfoStyles = createStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: tokens.spacing[4],
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.semantic.bg.infoSubtlest,
  },
  labelWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
  },
});

type AdditionalInfoProps = {
  openapiSku: string;
  customerSku: string;
  priceInfo: PriceInfo;
};

function AdditionalInfo({ priceInfo, openapiSku, customerSku }: AdditionalInfoProps) {
  const { classes } = useAdditionalInfoStyles();

  if (!priceInfo.usd) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Label size="lg">Additional Information</Label>
      <Divider />
      <RpiTextField label={`Base price (First ${priceInfo.minPageCount} pages)`}>
        {formatCurrency(priceInfo.usd.currency, priceInfo.usd.basePrice, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </RpiTextField>
      <RpiTextField label="Additional Price Per Page">
        {formatCurrency(priceInfo.usd.currency, priceInfo.usd.perPageCost, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </RpiTextField>
      <RpiTextField copyable label="Product SKU">
        {customerSku}
      </RpiTextField>
      <RpiTextField
        copyable
        label={
          <div className={classes.labelWithIcon}>
            Manufacturing SKU
            <TooltipRoot>
              <TooltipTrigger
                render={
                  <div>
                    <IconV3 icon="info" size={16} color={tokens.color.semantic.icon.subtle} />
                  </div>
                }
              />
              <TooltipContent>This is the SKU used by the printing manufacturer</TooltipContent>
            </TooltipRoot>
          </div>
        }
      >
        {openapiSku}
      </RpiTextField>
    </div>
  );
}
